import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  FolderOpen,
  Users,
  Cloud,
  Layers,
  Pen,
  Palette,
  Settings,
  RefreshCcw,
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import CountUp from '../../components/CountUp';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { documentService, apiService, cloudService } from '../../services/api';
import { settingsService } from '../../services/api/settings';
import '../../styles/background-effects.css';


interface OverviewStat {
  label: string;
  description: string;
  value: number;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface QuickLink {
  title: string;
  description: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

const QUICK_LINKS: QuickLink[] = [
  {
    title: 'Gestion de Connaissances',
    description: 'Parcourez et organisez tous vos documents.',
    href: '/workspace/documents',
    icon: FileText,
  },
  {
    title: 'Élaboration de Document',
    description: 'Rédaction cadrée ou libre assistée par IA.',
    href: '/workspace/redaction',
    icon: Pen,
  },
  {
    title: 'Support de Com',
    description: 'Créez vos contenus et supports visuels.',
    href: '/workspace/content',
    icon: Palette,
  },
  {
    title: 'Use Cases',
    description: 'Trouvez le bon scénario d’usage pour vos besoins.',
    href: '/workspace/usecases',
    icon: Layers,
  },
  {
    title: 'Équipes & Projets',
    description: 'Suivez l’avancement des projets et des équipes.',
    href: '/workspace/projects',
    icon: FolderOpen,
  },
  {
    title: 'Intégrations Cloud',
    description: 'Gérez les connexions et synchronisations cloud.',
    href: '/workspace/integrations',
    icon: Cloud,
  },
  {
    title: 'Paramètres',
    description: 'Gérez utilisateurs, permissions et préférences.',
    href: '/workspace/settings',
    icon: Settings,
  },
];

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [teams, setTeams] = useState<any[]>([]);
  const [useCases, setUseCases] = useState<any[]>([]);
  const [integrationsCount, setIntegrationsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [documentsData, projectsData, teamsData, useCasesData, cloudStatsData] = await Promise.all([
        documentService.getDocuments().catch(() => []),
        apiService.getProjects().catch(() => []),
        apiService.getTeams().catch(() => []),
        settingsService.getUseCases().catch(() => []),
        cloudService.getCloudStats().catch(() => ({})),
      ]);

      setDocuments(Array.isArray(documentsData) ? documentsData : []);
      setProjects(Array.isArray(projectsData) ? projectsData : []);
      setTeams(Array.isArray(teamsData) ? teamsData : []);
      setUseCases(Array.isArray(useCasesData) ? useCasesData : []);
      setIntegrationsCount(cloudStatsData?.total_connections ?? 0);
    } catch (err) {
      console.error('Erreur chargement tableau de bord:', err);
      setError("Impossible de récupérer les dernières données. Réessayez plus tard.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const overviewCards = useMemo(
    () => [
      {
        label: 'Documents Totaux',
        value: documents.length,
        change: '+12% ce mois',
        accent: 'teal-light',
        icon: FileText,
        textClass: 'text-teal-dark',
        bubbleClass: 'bg-teal-light',
      },
      {
        label: 'Projets Actifs',
        value: projects.length,
        change: '+3 nouveaux',
        accent: 'teal-dark',
        icon: FolderOpen,
        textClass: 'text-teal-dark',
        bubbleClass: 'bg-teal-dark',
      },
      {
        label: 'Équipes',
        value: teams.length,
        change: `${teams.reduce((acc, team) => acc + (Array.isArray(team.members) ? team.members.length : 0), 0)} membres` || 'Collaborateurs actifs',
        accent: 'teal-600',
        icon: Users,
        textClass: 'text-teal-600',
        bubbleClass: 'bg-teal-600',
      },
      {
        label: 'Intégrations',
        value: integrationsCount,
        change: integrationsCount > 0 ? 'Toutes actives' : 'Aucune active',
        accent: 'teal-800',
        icon: Cloud,
        textClass: 'text-teal-800',
        bubbleClass: 'bg-teal-800',
      },
    ],
    [documents.length, projects.length, teams, integrationsCount]
  );

  const recentDocuments = useMemo(
    () =>
      documents
        .slice()
        .sort(
          (a, b) =>
            new Date(b.uploaded_at || 0).getTime() -
            new Date(a.uploaded_at || 0).getTime()
        )
        .slice(0, 5),
    [documents]
  );

  const recentProjects = useMemo(
    () =>
      projects
        .slice()
        .sort(
          (a, b) =>
            new Date(b.updated_at || b.created_at || 0).getTime() -
            new Date(a.updated_at || a.created_at || 0).getTime()
        )
        .slice(0, 4),
    [projects]
  );

  return (
    <DashboardLayout>

      <div className="space-y-6 content-above-aurora relative z-10">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Tableau de bord</h1>
            <p className="text-gray-600">
              Vue d’ensemble des espaces disponibles dans omnia-eg.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            icon={<RefreshCcw className="w-4 h-4" />}
            onClick={loadData}
            disabled={loading}
          >
            Actualiser
          </Button>
        </div>

        {error && (
          <Card className="border-red-200 bg-red-50 text-red-700">
            <p>{error}</p>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {overviewCards.map((card, index) => (
            <motion.div
              key={card.label}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: index * 0.05 }}
            >
              <div className=" bg-white rounded-xl shadow-lg border border-gray-100 hover:shadow-xl hover:scale-[1.02] transition-all duration-300 p-6 relative overflow-hidden ">
                <div className={`absolute top-0 right-0 w-20 h-20 ${card.bubbleClass} rounded-bl-full opacity-10`}></div>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-600 mb-1">{card.label}</p>
                    <p className="text-2xl font-bold text-gray-900 mb-2">
                      <span className="text-2xl font-bold text-gray-900">
                        {loading ? '—' : card.value.toLocaleString('fr-FR')}
                      </span>
                    </p>
                    <p className={`text-sm font-medium ${card.textClass}`}>
                      {card.change}
                    </p>
                  </div>
                  <div className={`${card.bubbleClass} bg-opacity-20 p-3 rounded-lg`}>
                    <card.icon className={`w-6 h-6 ${card.textClass}`} />
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Navigation rapide</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {QUICK_LINKS.map((link) => (
              <Card
                key={link.href}
                hoverable
                onClick={() => navigate(link.href)}
                className="flex h-full flex-col justify-between"
              >
                <div className="flex items-start gap-3">
                  <div className="p-3 rounded-lg bg-teal-light/15 text-teal-dark">
                    <link.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{link.title}</h3>
                    <p className="mt-1 text-sm text-gray-600">{link.description}</p>
                  </div>
                </div>
                <div className="mt-4 text-sm font-medium text-teal-dark">
                  Accéder &rarr;
                </div>
              </Card>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Derniers documents</h3>
                <p className="text-sm text-gray-500">
                  Les fichiers les plus récents importés dans la plateforme.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/workspace/documents')}
              >
                Voir tout
              </Button>
            </div>

            {loading && documents.length === 0 ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : recentDocuments.length === 0 ? (
              <div className="text-center py-10 text-gray-500">
                Aucun document disponible pour le moment.
              </div>
            ) : (
              <ul className="space-y-3">
                {recentDocuments.map((doc) => (
                  <li
                    key={doc.id}
                    className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 px-3 py-3"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-gray-900">
                        {doc.name || doc.title}
                      </p>
                      <p className="text-xs text-gray-500">
                        {doc.file_size_human ??
                          (doc.size ? `${Math.round(doc.size / 1024)} KB` : 'Taille inconnue')}
                        {' · '}
                        {doc.uploaded_at
                          ? new Date(doc.uploaded_at).toLocaleString('fr-FR')
                          : 'Date inconnue'}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/workspace/documents')}
                    >
                      Ouvrir
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Projets récents</h3>
                <p className="text-sm text-gray-500">
                  Dernières initiatives créées ou mises à jour.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/workspace/projects')}
              >
                Aller aux projets
              </Button>
            </div>

            {loading && projects.length === 0 ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : recentProjects.length === 0 ? (
              <div className="text-center py-10 text-gray-500">
                Aucun projet actif actuellement.
              </div>
            ) : (
              <ul className="space-y-3">
                {recentProjects.map((project) => (
                  <li
                    key={project.id}
                    className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-3"
                  >
                    <p className="text-sm font-medium text-gray-900">{project.name}</p>
                    <p className="text-xs text-gray-500">
                      Mise à jour le{' '}
                      {new Date(project.updated_at || project.created_at).toLocaleDateString('fr-FR')}
                    </p>
                    {Array.isArray(project.documents) && (
                      <p className="mt-1 text-xs text-gray-400">
                        {project.documents.length} document(s) liés
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DashboardPage;
